import { Clipboard } from "@capacitor/clipboard";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import styles from "@components/ArticleTableRow.module.css";
import Button from "@components/buttons/Button";
import CircleButton from "@components/buttons/CircleButton";
import DateAndTimeStamp from "@components/dates/DateAndTimeStamp";
import DateStamp from "@components/dates/DateStamp";
import DeleteConfirmationAlert from "@components/DeleteConfirmationAlert";
import Nbsp from "@components/Nbsp";
import TagList from "@components/TagList";
import Icon from "@components/text/Icon";
import TextWithIcon from "@components/text/TextWithIcon";
import InternalLink from "@components/typography/InternalLink";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyExpert from "@hooks/useMyExpert";
import useOpener from "@hooks/useOpener";
import {
  ArticleContentTypeEnum,
  ArticleDestroyDocument,
  ArticleDestroyMutation,
  ArticleDestroyMutationVariables,
  GetArticlesQuery
} from "@typing/Generated";
import { articleUrlPrefix } from "@utils/articleUtils";
import { countryNameForCode } from "@utils/countryUtils";
import { journeyRoute } from "@utils/routeUtils";

type GetArticlesQueryArticle = NonNullable<
  NonNullable<NonNullable<GetArticlesQuery["articles"]["edges"]>[number]>["node"]
>;

type Props = {
  article: GetArticlesQueryArticle;
  journeyId: string | null;
  onSelect?: (article: { id: string; title: string }) => void;
  selectedArticleIds?: string[];
  showMyStoryFields?: boolean;
  showWebinarFields?: boolean;
};

const ArticleTableRow = ({
  article,
  journeyId,
  onSelect,
  selectedArticleIds,
  showMyStoryFields = false,
  showWebinarFields = false
}: Props) => {
  const intl = useIntl();
  const deleteOpener = useOpener();
  const myExpert = useMyExpert();
  const [tooltipActive, setTooltipActive] = useState(false);

  const [destroyArticle, client] = useMutationWithValidation<ArticleDestroyMutation, ArticleDestroyMutationVariables>(
    ArticleDestroyDocument,
    "article",
    {
      update: () => {
        client.resetStore();
      }
    }
  );

  const handleDelete = useCallback(() => {
    destroyArticle({
      id: article.id
    });
  }, [article.id, destroyArticle]);

  const handleCopyToClipboard = useCallback(() => {
    const articleUrl = `${import.meta.env.VITE_APP_HOST}${
      window.location.pathname.replace("/expert", "/client") + `/${article.id}`
    }`;
    Clipboard.write({ string: articleUrl }).then(() => {
      setTooltipActive(true);
    });
  }, [article.id]);

  const messagePrefix = useMemo(() => {
    if (article.contentType === ArticleContentTypeEnum.MY_STORY) {
      return "myStory";
    }
    if (article.contentType === ArticleContentTypeEnum.WEBINAR) {
      return "webinar";
    }
    return "article";
  }, [article]);

  useEffect(() => {
    const tooltipTimeout = setTimeout(() => setTooltipActive(false), 1500);
    return () => {
      clearTimeout(tooltipTimeout);
    };
  }, [tooltipActive]);

  const contentTypeIcon = useMemo(() => {
    if (article.contentType === ArticleContentTypeEnum.MY_STORY) {
      return "handHeart";
    } else if (article.contentType === ArticleContentTypeEnum.TEXT) {
      return "newspaper";
    } else if (article.contentType === ArticleContentTypeEnum.VIDEO) {
      return "video";
    } else if (article.contentType === ArticleContentTypeEnum.WEBINAR) {
      return "videoPlus";
    }
    return "newspaper";
  }, [article.contentType]);

  return (
    <tr data-test="article-row">
      <td>
        <TextWithIcon
          icon={contentTypeIcon}
          iconFirst
          text={intl.formatMessage({ id: `models.article.contentType.${article.contentType}` })}
        />
      </td>
      <td>
        <InternalLink
          data-test="article-link"
          to={
            myExpert && journeyId
              ? journeyRoute({ journeyId, linkAfterJourney: `/${articleUrlPrefix(article)}/${article.id}`, myExpert })
              : `/admin/${articleUrlPrefix(article)}/${article.id}`
          }
        >
          {article.title}
        </InternalLink>
      </td>
      <td>
        <TagList tags={article.tags} />
      </td>
      {showWebinarFields && (
        <td>{article.webinarStartTime && <DateAndTimeStamp showWeekday={false} value={article.webinarStartTime} />}</td>
      )}
      <td>
        <DateStamp showWeekday={false} value={article.createdAt} />
      </td>
      <td>
        <DateStamp showWeekday={false} value={article.updatedAt} />
      </td>
      <td>{article.author.fullName}</td>
      {!showMyStoryFields && !showWebinarFields && (
        <td>
          {article.focusAreas.map(focusArea => intl.formatMessage({ id: `models.focusArea.${focusArea}` })).join(", ")}
        </td>
      )}
      <td>
        <div className={styles.countries}>
          {article.countryCodes.length > 0 ? (
            article.countryCodes.map((countryCode, index) => (
              <span key={countryCode}>
                {countryNameForCode(countryCode)}
                {index !== article.countryCodes.length - 1 && (
                  <>
                    <FormattedMessage id="dictionary.punctuation.comma" />
                    <Nbsp />
                  </>
                )}
              </span>
            ))
          ) : (
            <FormattedMessage id="pages.admin.articles.global" />
          )}
        </div>
      </td>
      {showWebinarFields && (
        <td>
          {article.webinarCompleted ? (
            <FormattedMessage id="dictionary.library" />
          ) : (
            <>
              {article.webinarStartTime && (
                <>
                  {article.webinarStartTime < new Date() ? (
                    <FormattedMessage id="dictionary.complete" />
                  ) : (
                    <FormattedMessage id="dictionary.upcoming" />
                  )}
                </>
              )}
            </>
          )}
        </td>
      )}
      <td>{article.hidden && <Icon icon="check" />}</td>
      <td>
        {onSelect && selectedArticleIds ? (
          <div className="ion-margin-end">
            <Button disabled={selectedArticleIds.includes(article.id)} onClick={() => onSelect(article)}>
              <FormattedMessage id={selectedArticleIds.includes(article.id) ? "dictionary.added" : "dictionary.add"} />
            </Button>
          </div>
        ) : myExpert ? (
          <div className="ion-margin-end">
            <CircleButton
              ariaLabelKey="accessibility.ariaLabels.copy"
              fill="clear"
              icon="link"
              onClick={handleCopyToClipboard}
            />
            {tooltipActive && (
              <div className={styles.tooltipTop} data-test="copy-tooltip">
                <div className={styles.tooltipMessage}>
                  <FormattedMessage id="pages.expert.sharableUrls.copied" />
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            <CircleButton
              ariaLabelKey="accessibility.ariaLabels.edit"
              fill="clear"
              icon="edit"
              routerLink={`/admin/${articleUrlPrefix(article)}/${article.id}/edit`}
            />
            <br />
            <CircleButton
              ariaLabelKey="accessibility.ariaLabels.delete"
              fill="clear"
              icon="trash"
              onClick={deleteOpener.open}
            />
            {showWebinarFields && (
              <>
                <br />
                <CircleButton
                  ariaLabelKey="accessibility.ariaLabels.registrations"
                  fill="clear"
                  icon="ticket"
                  routerLink={`/admin/${articleUrlPrefix(article)}/${article.id}/webinar-registrations`}
                />
              </>
            )}
          </>
        )}
      </td>
      <DeleteConfirmationAlert
        messageKey={`pages.${messagePrefix}.reallyDelete`}
        onDelete={handleDelete}
        opener={deleteOpener}
      />
    </tr>
  );
};

export default ArticleTableRow;
